// New sandbox (ui-spec §6.6, Run code): one compact form over the Sandbox API shape, so the YAML, CLI and Python
// tabs show exactly what Create sends. Create is a plain POST of the manifest to the project's sandboxes collection.
import { collectionPath, newIdempotencyKey, useApi, useKList, type KObject } from "@nodus/api";
import { Button, Callout, Field, generatedName, groupOfferings, Input, ManifestTabs, nameProblem, Page, PageHeader, Select, usePrefs, type Manifest, type OfferingLike } from "@nodus/ui";
import { useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { ApiErrorCallout } from "../../app/api-error";
import { CreationSteps, ManifestReview } from "../../app/creation-steps";
import { SANDBOX_DEFAULTS, type Scope, type SandboxSpec } from "./model";

type SandboxManifest = Manifest & { metadata: { name: string }; spec: SandboxSpec };

export function SandboxNew({ scope }: { scope: Scope }) {
  const { client } = useApi();
  const offerings = useKList("Offering", { org: scope.org }, { watch: false });
  const prefs = usePrefs();
  const navigate = useNavigate();
  const [m, setM] = React.useState<SandboxManifest>(() => ({
    apiVersion: "nodus.dev/v1",
    kind: "Sandbox",
    metadata: { name: generatedName("sandbox"), namespace: scope.project },
    spec: {
      image: SANDBOX_DEFAULTS.image,
      resources: { cpu: SANDBOX_DEFAULTS.cpu, memory: SANDBOX_DEFAULTS.memory, disk: SANDBOX_DEFAULTS.disk },
      network: { egress: { policy: SANDBOX_DEFAULTS.egress } },
      lifecycle: { idleTimeout: SANDBOX_DEFAULTS.idleTimeout },
    },
  }));
  const attempt = React.useRef<{ body: string; key: string } | undefined>(undefined);
  const submitting = React.useRef(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<unknown>();
  const gpuFamilies = groupOfferings((offerings.data?.items ?? []) as OfferingLike[]);
  const update = (patch: (x: SandboxManifest) => void) => {
    const next = structuredClone(m);
    patch(next);
    setM(next);
  };
  const problem = nameProblem(m.metadata.name);
  const resources = m.spec.resources ?? {};
  const gpu = resources.gpu;
  const selectedGPUFamily = gpu?.type?.[0] ?? "__any";
  const gpuFamilyOptions = [
    { value: "__any", label: "Any GPU (minimum 24 GiB)" },
    ...gpuFamilies.map((family) => ({ value: family.family, label: family.name })),
  ];
  if (selectedGPUFamily !== "__any" && !gpuFamilyOptions.some((option) => option.value === selectedGPUFamily)) {
    gpuFamilyOptions.push({ value: selectedGPUFamily, label: selectedGPUFamily });
  }

  const create = async () => {
    if (submitting.current) return;
    submitting.current = true;
    const body = JSON.stringify(m);
    if (attempt.current?.body !== body) attempt.current = { body, key: newIdempotencyKey() };
    setBusy(true);
    setError(undefined);
    try {
      const res = await client.post<KObject>(collectionPath("Sandbox", scope.project), m, { org: scope.org, idempotencyKey: attempt.current.key });
      void navigate({ to: "/$org/$project/sandboxes/$name/$tab", params: { ...scope, name: res.data.metadata.name, tab: "overview" } });
    } catch (err) {
      setError(err);
      submitting.current = false;
      setBusy(false);
    }
  };

  const text = (key: "cpu" | "memory" | "disk") => (e: React.ChangeEvent<HTMLInputElement>) =>
    update((x) => void (e.target.value ? (x.spec.resources = { ...x.spec.resources, [key]: e.target.value }) : delete x.spec.resources?.[key]));
  const setGPUEnabled = (enabled: boolean) => update((x) => {
    x.spec.resources = { ...x.spec.resources };
    if (enabled) {
      x.spec.resources.gpu ??= { count: 1, minMemory: "24Gi" };
      x.spec.continuity = { ...x.spec.continuity, mode: "Ephemeral" };
    } else {
      delete x.spec.resources.gpu;
      delete x.spec.fuseMount;
      delete x.spec.continuity;
    }
  });
  const setGPUFamily = (family: string) => update((x) => {
    const next = { ...x.spec.resources?.gpu, count: x.spec.resources?.gpu?.count ?? 1 };
    if (family === "__any") {
      delete next.type;
      delete next.exact;
      next.minMemory ??= "24Gi";
    } else {
      next.type = [family];
      delete next.exact;
      delete next.minMemory;
    }
    x.spec.resources = { ...x.spec.resources, gpu: next };
  });
  const form = (
    <div className="grid gap-4">
      <Field id="sb-name" label="Sandbox name" error={problem} required>
        <Input value={m.metadata.name} className="font-mono" onChange={(e) => update((x) => void (x.metadata.name = e.target.value))} />
      </Field>
      <Field id="sb-image" label="Image" required>
        <Input value={m.spec.image ?? ""} className="font-mono" onChange={(e) => update((x) => void (x.spec.image = e.target.value))} />
      </Field>

    </div>
  );

  // Render the complete submitted manifest so imported fields survive every interface.
  return (
    <Page>
      <PageHeader title="New sandbox" parents={[{ label: "Runs", href: `/${encodeURIComponent(scope.org)}/${encodeURIComponent(scope.project)}/runs` }]} />
      <CreationSteps invalid={Boolean(problem) || !m.spec.image} busy={busy}
        configure={<ManifestTabs manifest={m} onManifestChange={(next) => setM(next as SandboxManifest)} scope={scope} omitScope={prefs.omitScope} form={form} />}
        resources={<div className="grid max-w-3xl gap-4 rounded-lg border border-line bg-card p-5">
      <Field id="sb-gpu-enabled" label="GPU" hint="GPU Sandboxes use a dedicated, non-interruptible on-demand GPU host, subject to available capacity and provider pricing.">
        <input type="checkbox" checked={Boolean(gpu)} onChange={(e) => setGPUEnabled(e.target.checked)} />
      </Field>
      {gpu ? <Field id="sb-max-cost" label="Maximum cost (USD)" hint="Optional total spend limit across this Sandbox's starts.">
        <Input inputMode="decimal" value={m.spec.maxCostUSD ?? ""} className="font-mono" onChange={(e) => update((x) => void (e.target.value ? (x.spec.maxCostUSD = e.target.value) : delete x.spec.maxCostUSD))} />
      </Field> : null}
      {gpu ? <>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="sb-gpu-family" label="GPU family">
            <Select id="sb-gpu-family" value={selectedGPUFamily} onValueChange={setGPUFamily} options={gpuFamilyOptions} />
          </Field>
          <Field id="sb-gpu-count" label="GPUs per host">
            <Select id="sb-gpu-count" value={String(gpu.count ?? 1)} onValueChange={(v) => update((x) => {
              x.spec.resources = { ...x.spec.resources, gpu: { ...x.spec.resources?.gpu, count: Number(v) } };
            })} options={[1, 2, 4, 8].map((n) => ({ value: String(n), label: `${n} GPU${n > 1 ? "s" : ""}` }))} />
          </Field>
        </div>
        {selectedGPUFamily === "__any" ? <p className="text-small text-fg-muted">Nodus will match a GPU with at least 24 GiB of memory. Choose a family above to narrow the request.</p> : null}
        <Field id="sb-continuity" label="Saved files" hint="Ephemeral is the default. Local-directory recovery is Beta; the API validates the selected path and runtime.">
          <Select id="sb-continuity" value={m.spec.continuity?.mode ?? "Ephemeral"} onValueChange={(mode) => update((x) => {
            x.spec.continuity = mode === "Snapshotted" ? { mode: "Snapshotted", stateDir: x.spec.continuity?.stateDir ?? "/nodus/state" } : { mode: "Ephemeral" };
          })} options={[{ value: "Ephemeral", label: "Ephemeral" }, { value: "Snapshotted", label: "Local directory (Beta)" }]} />
        </Field>
        {m.spec.continuity?.mode === "Snapshotted" ? <Field id="sb-state-dir" label="State directory (Beta)" hint="An absolute local path, initially empty. Keep remote mounts outside this directory. The path cannot change after creation.">
          <Input value={m.spec.continuity.stateDir ?? ""} className="font-mono" onChange={(e) => update((x) => { x.spec.continuity = { ...x.spec.continuity, mode: "Snapshotted", stateDir: e.target.value }; })} />
        </Field> : null}
        <Callout tone="neutral" title={m.spec.continuity?.mode === "Snapshotted" ? "Local-directory recovery (Beta)" : "Ephemeral GPU Sandbox"}>
          {m.spec.continuity?.mode === "Snapshotted" ? "Only the selected local directory is saved. A start restores files before launching a fresh process; GPU memory, processes and the rest of the filesystem are not restored. Failed or empty final saves retain the last useful snapshot; check StateSaved after stopping. " : "Local files are discarded on stop. "}
          FUSE only provides the guest <code>/dev/fuse</code> capability; bring your own JuiceFS client, credentials, and mount setup on each start.
        </Callout>
        <Field id="sb-fuse-mount" label="FUSE mounts" hint="Optional. Uses the gVisor GPU runtime; Nodus does not provision a mount or filesystem.">
          <input type="checkbox" checked={Boolean(m.spec.fuseMount)} onChange={(e) => update((x) => e.target.checked ? (x.spec.fuseMount = true) : delete x.spec.fuseMount)} />
        </Field>
      </> : null}
      <div className="grid gap-4 sm:grid-cols-3">
        <Field id="sb-cpu" label="CPU (vCPU)">
          <Input inputMode="decimal" value={resources.cpu ?? ""} className="font-mono" onChange={text("cpu")} />
        </Field>
        <Field id="sb-memory" label="Memory">
          <Input value={resources.memory ?? ""} className="font-mono" onChange={text("memory")} />
        </Field>
        <Field id="sb-disk" label="Disk">
          <Input value={resources.disk ?? ""} className="font-mono" onChange={text("disk")} />
        </Field>
      </div>
      <Field id="sb-egress" label="Outbound network" hint="Open allows the internet; private addresses stay blocked.">
        <Select
          id="sb-egress"
          value={m.spec.network?.egress?.policy ?? SANDBOX_DEFAULTS.egress}
          onValueChange={(v) => update((x) => void (x.spec.network = { egress: { policy: v as "Deny" | "Open" } }))}
          options={[{ value: "Deny", label: "Deny (default)" }, { value: "Open", label: "Open" }]}
        />
      </Field>
      <Field id="sb-idle" label="Idle timeout">
        <Input value={m.spec.lifecycle?.idleTimeout ?? ""} className="font-mono" onChange={(e) => update((x) => void (e.target.value ? (x.spec.lifecycle = { ...x.spec.lifecycle, idleTimeout: e.target.value }) : delete x.spec.lifecycle?.idleTimeout))} />
      </Field>
</div>}
        review={<><ManifestReview manifest={m} />{error ? <ApiErrorCallout error={error} scope={scope} /> : null}</>}
        launch={<Button data-primary-action disabled={Boolean(problem) || !m.spec.image || busy} onClick={() => void create()}>{busy ? "Creating…" : "Create sandbox"}</Button>}
      />
    </Page>
  );
}
